import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Instagram, Facebook, CheckCircle, AlertCircle, RefreshCw, Zap, Copy, Key, Shield } from 'lucide-react';
import {
  fetchIGAccount, startConnect, fetchPendingPages, finalizeConnect, disconnectAccount, clearConnectUrl,
  fetchUrls, fetchAppCredentials, saveAppCredentials,
} from '../../redux/instagramSlice';
import alertService from '../../utils/alert';

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#E5E5E5]">
        <Icon size={16} className="text-[#E1306C]" />
        <span className="text-[13px] font-bold text-[#2E2E2E]">{title}</span>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-semibold text-[#706E6B] block">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[12px] font-mono text-[#0176D3] bg-[#F4F6F9] border border-[#E5E5E5] rounded-[4px] px-3 py-2 break-all">
          {value}
        </code>
        <button onClick={copy} className="flex-shrink-0 p-2 border border-[#E5E5E5] hover:border-[#0176D3] rounded-[4px] text-[#5C5C5C] hover:text-[#0176D3]">
          <Copy size={14} />
        </button>
      </div>
      {copied && <p className="text-[11px] text-[#2E844A]">Copied!</p>}
    </div>
  );
}

const INPUT = 'w-full border border-[#E5E5E5] rounded-[4px] px-3 py-2 text-[13px] text-[#2E2E2E] focus:outline-none focus:border-[#0176D3]';

function randomToken() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function InstagramSettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { account, accountLoading, pendingPages, connectUrl, urls, credential, error } = useSelector((s) => s.instagram);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [loginType, setLoginType] = useState('instagram_login');
  const [form, setForm] = useState({ appId: '', appSecret: '', webhookVerifyToken: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const oauthError = searchParams.get('error');
  const pendingId = searchParams.get('pendingId');
  const justConnected = searchParams.get('connected');

  const load = useCallback(() => dispatch(fetchIGAccount()), [dispatch]);

  useEffect(() => { load(); dispatch(fetchUrls()); dispatch(fetchAppCredentials()); }, [load, dispatch]);

  // Prefill the form (except the secret, which the server never returns) once
  // credentials load, and default the selected tab to whatever's already saved.
  useEffect(() => {
    if (credential) {
      setLoginType(credential.loginType);
      setForm({ appId: credential.appId || '', appSecret: '', webhookVerifyToken: credential.webhookVerifyToken || '' });
    }
  }, [credential]);

  useEffect(() => {
    if (pendingId) dispatch(fetchPendingPages(pendingId));
  }, [pendingId, dispatch]);

  useEffect(() => {
    if (justConnected) {
      load();
      setSearchParams({}, { replace: true });
    }
  }, [justConnected, load, setSearchParams]);

  useEffect(() => {
    if (connectUrl) {
      window.location.href = connectUrl;
      dispatch(clearConnectUrl());
    }
  }, [connectUrl, dispatch]);

  const switchLoginType = (type) => {
    setLoginType(type);
    if (credential?.loginType === type) {
      setForm({ appId: credential.appId || '', appSecret: '', webhookVerifyToken: credential.webhookVerifyToken || '' });
    } else {
      setForm({ appId: '', appSecret: '', webhookVerifyToken: randomToken() });
    }
    setSaved(false);
  };

  const saveCredentials = async () => {
    setSaving(true);
    const result = await dispatch(saveAppCredentials({ loginType, ...form }));
    setSaving(false);
    if (!result.error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const connect = async (type) => {
    setConnecting(type);
    await dispatch(startConnect(type));
    setConnecting(null);
  };

  const choosePage = async (pageId) => {
    await dispatch(finalizeConnect({ pendingId, pageId }));
    setSearchParams({}, { replace: true });
  };

  const disconnect = async () => {
    if (!(await alertService.confirm('Disconnect?', 'Disconnect this Instagram account? DMs, comments, and publishing will stop working until you reconnect.', 'danger', 'Disconnect'))) return;
    setDisconnecting(true);
    await dispatch(disconnectAccount());
    setDisconnecting(false);
  };

  if (accountLoading && !account) {
    return <div className="py-10 px-4 text-[13px] text-[#5C5C5C]">Loading Instagram settings…</div>;
  }

  const hasCredentialForSelectedType = credential?.loginType === loginType && !!credential?.appId;

  return (
    <div className="space-y-5">
      {oauthError && (
        <div className="flex items-start gap-3 bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#BA0517] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#8E030F]">{decodeURIComponent(oauthError)}</div>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-3 bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#BA0517] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#8E030F]">{error}</div>
        </div>
      )}

      {pendingId && pendingPages.length > 0 && (
        <Section title="Choose a Facebook Page" icon={Facebook}>
          <p className="text-[13px] text-[#5C5C5C]">
            Multiple Facebook Pages with a linked Instagram account were found. Pick the one to connect.
          </p>
          <div className="space-y-2">
            {pendingPages.map((p) => (
              <button
                key={p.id}
                onClick={() => choosePage(p.id)}
                className="w-full flex items-center justify-between border border-[#E5E5E5] hover:border-[#0176D3] rounded-[4px] px-4 py-3 text-left transition-colors"
              >
                <div>
                  <div className="text-[13px] font-semibold text-[#2E2E2E]">{p.name}</div>
                  <div className="text-[12px] text-[#5C5C5C]">@{p.igUsername}</div>
                </div>
                <span className="text-[12px] font-semibold text-[#0176D3]">Connect</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {account ? (
        <Section title="Connected Account" icon={Instagram}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {account.profilePictureUrl && (
                <img src={account.profilePictureUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
              )}
              <div>
                <div className="text-[13px] font-bold text-[#2E2E2E]">@{account.username}</div>
                <div className="text-[12px] text-[#5C5C5C]">
                  {account.loginType === 'instagram_login' ? 'Instagram Login' : 'Facebook Login for Business'}
                  {' · '}
                  <span className={account.status === 'active' ? 'text-[#2E844A]' : 'text-[#BA0517]'}>{account.status}</span>
                </div>
              </div>
            </div>
            <button
              onClick={disconnect}
              disabled={disconnecting}
              className="text-[13px] font-semibold text-[#BA0517] hover:text-[#8E030F] disabled:opacity-60"
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>

          {account.reconnectRequired && (
            <div className="flex items-start gap-3 bg-[#FFF4E5] border border-[#FFB75D] rounded-[4px] px-4 py-3">
              <RefreshCw size={16} className="text-[#DD7A01] mt-0.5 flex-shrink-0" />
              <div className="text-[13px] text-[#8C4B02]">
                <strong>Reconnect needed.</strong> Instagram couldn't silently refresh your access — reconnect below to keep
                DMs, comments, and publishing working.
              </div>
            </div>
          )}

          {account.tokenExpiresAt && (
            <div className="text-[12px] text-[#5C5C5C]">
              Token expires: {new Date(account.tokenExpiresAt).toLocaleDateString()}
            </div>
          )}
        </Section>
      ) : (
        !pendingId && (
          <Section title="Connect Instagram" icon={Zap}>
            <p className="text-[13px] text-[#5C5C5C]">
              This CRM connects to Instagram using <strong>your own</strong> Meta Developer App — not a shared app used by
              every customer. If you haven't created one yet, go to{' '}
              <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="underline font-semibold text-[#0176D3]">
                developers.facebook.com/apps
              </a>{' '}
              and create one first.
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => switchLoginType('instagram_login')}
                className={`flex-1 flex items-center justify-center gap-2 rounded-[4px] px-4 py-2.5 text-[13px] font-semibold border transition-colors  whitespace-nowrap ${
                  loginType === 'instagram_login' ? 'border-[#E1306C] bg-[#FDF2F8] text-[#E1306C]' : 'border-[#E5E5E5] text-[#5C5C5C] hover:border-[#E1306C]'
                }`}
              >
                <Instagram size={16} /> Instagram Login
              </button>
              <button
                onClick={() => switchLoginType('facebook_login')}
                className={`flex-1 flex items-center justify-center gap-2 rounded-[4px] px-4 py-2.5 text-[13px] font-semibold border transition-colors  whitespace-nowrap ${
                  loginType === 'facebook_login' ? 'border-[#1877F2] bg-[#EFF6FF] text-[#1877F2]' : 'border-[#E5E5E5] text-[#5C5C5C] hover:border-[#1877F2]'
                }`}
              >
                <Facebook size={16} /> Facebook Login
              </button>
            </div>
            <p className="text-[11px] text-[#5C5C5C]">
              {loginType === 'instagram_login'
                ? 'Native Instagram login — no Facebook Page required. In your app: Instagram → API setup with Instagram login → Business login settings.'
                : 'Requires a Facebook Page linked to the Instagram account. In your app: add the Facebook Login for Business product.'}
            </p>

            {urls && (
              <div className="space-y-3 border-t border-[#E5E5E5] pt-4">
                <p className="text-[12px] font-bold text-[#2E2E2E]">1. Add these to your Meta App Dashboard</p>
                <CopyField label="OAuth Redirect URI" value={urls.callbackUrl} />
                <CopyField label="Webhook Callback URL" value={urls.webhookUrl} />
              </div>
            )}

            <div className="space-y-3 border-t border-[#E5E5E5] pt-4">
              <p className="text-[12px] font-bold text-[#2E2E2E]">2. Enter your app's credentials</p>
              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">App ID</label>
                <div className="relative">
                  <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                  <input className={`${INPUT} pl-9`} value={form.appId} onChange={(e) => setForm((f) => ({ ...f, appId: e.target.value }))} placeholder="990602627938098" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">App Secret</label>
                <div className="relative">
                  <Shield size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                  <input
                    type="password"
                    className={`${INPUT} pl-9`}
                    value={form.appSecret}
                    onChange={(e) => setForm((f) => ({ ...f, appSecret: e.target.value }))}
                    placeholder={hasCredentialForSelectedType ? '••••••••••••••••' : 'your app secret'}
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">Webhook Verify Token</label>
                <input
                  className={INPUT}
                  value={form.webhookVerifyToken}
                  onChange={(e) => setForm((f) => ({ ...f, webhookVerifyToken: e.target.value }))}
                  placeholder="a string you choose"
                />
                <p className="text-[11px] text-[#5C5C5C] mt-1">Enter this exact same value in the Webhooks product's Verify Token field.</p>
              </div>
              <button
                onClick={saveCredentials}
                disabled={saving || !form.appId || !form.webhookVerifyToken || (!form.appSecret && !hasCredentialForSelectedType)}
                className="w-full bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2.5 rounded-[4px]"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saved && <p className="text-[12px] text-[#2E844A] flex items-center gap-1"><CheckCircle size={13} /> Saved</p>}
            </div>

            {hasCredentialForSelectedType && (
              <div className="border-t border-[#E5E5E5] pt-4">
                <p className="text-[12px] font-bold text-[#2E2E2E] mb-3">3. Connect your account</p>
                <button
                  onClick={() => connect(loginType)}
                  disabled={connecting === loginType}
                  className="w-full flex items-center justify-center gap-2 border border-[#E5E5E5] hover:border-[#E1306C] rounded-[4px] px-4 py-3 text-[13px] font-semibold text-[#2E2E2E] transition-colors disabled:opacity-60"
                >
                  {loginType === 'instagram_login' ? <Instagram size={16} className="text-[#E1306C]" /> : <Facebook size={16} className="text-[#1877F2]" />}
                  {connecting === loginType ? 'Redirecting…' : `Connect with ${loginType === 'instagram_login' ? 'Instagram' : 'Facebook Page'}`}
                </button>
              </div>
            )}
          </Section>
        )
      )}

      {account && (
        <Section title="Automation" icon={Zap}>
          <p className="text-[13px] text-[#5C5C5C]">
            Configure auto-reply rules for comments and DMs — including rules scoped to a specific
            post or campaign — from{' '}
            <button onClick={() => navigate('/dashboard/instagram/automation')} className="underline font-semibold text-[#0176D3]">
              Automation Rules
            </button>. General-purpose AI DM handling (when no rule matches) is configured under{' '}
            <button onClick={() => navigate('/dashboard/support/widget')} className="underline font-semibold text-[#0176D3]">
              Support Widget
            </button>{' '}
            settings.
          </p>
        </Section>
      )}
    </div>
  );
}
